import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowLeft, 
  Calendar, 
  User, 
  Eye,
  Share2,
  Clock,
  ChevronRight,
  Facebook,
  Twitter,
  Linkedin,
  Link2,
  Check
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';
import Footer from '@/components/Footer';

const BlogPostPage = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/api/blog/${slug}`);
      setPost(response.data);
      
      // Fetch related posts from same category
      if (response.data.category) {
        const relatedResponse = await api.get(`/api/blog?category=${encodeURIComponent(response.data.category)}&limit=3`);
        setRelatedPosts(relatedResponse.data.posts.filter(p => p.post_id !== response.data.post_id).slice(0, 2));
      }
    } catch (error) {
      toast.error('Post não encontrado');
      navigate('/blog');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const getReadingTime = (content) => {
    const text = content.replace(/<[^>]*>/g, '');
    const words = text.split(/\s+/).length;
    const minutes = Math.ceil(words / 200);
    return `${minutes} min de leitura`;
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareTitle = post?.title || '';

  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`,
    linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareTitle)}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(shareTitle + ' ' + shareUrl)}`
  };

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success('Link copiado!');
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-[#002FA7] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!post) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#F9F9F8] flex flex-col">
      <Helmet>
        <title>{post.title} | Blog Quero Publicar</title>
        <meta name="description" content={post.excerpt || post.content?.substring(0, 160)} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt || post.content?.substring(0, 160)} />
        {post.cover_image && <meta property="og:image" content={post.cover_image} />}
        <meta property="og:type" content="article" />
        <meta property="article:published_time" content={post.published_at} />
        <meta property="article:author" content={post.author} />
        <meta property="article:section" content={post.category} />
        {post.tags?.map((tag, i) => (
          <meta key={i} property="article:tag" content={tag} />
        ))}
      </Helmet>

      {/* Header */}
      <header className="bg-white border-b border-[#E4E4E7] sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate('/blog')}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Voltar ao Blog
            </Button>
            
            {/* Share buttons */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500 hidden sm:inline">Compartilhar:</span>
              <Button variant="ghost" size="icon" onClick={() => window.open(shareLinks.facebook, '_blank')}>
                <Facebook className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => window.open(shareLinks.twitter, '_blank')}>
                <Twitter className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => window.open(shareLinks.linkedin, '_blank')}>
                <Linkedin className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={copyLink}>
                {copied ? <Check className="h-4 w-4 text-green-500" /> : <Link2 className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1">
        {/* Hero */}
        {post.cover_image && (
          <div className="w-full h-64 md:h-96 overflow-hidden">
            <img 
              src={post.cover_image} 
              alt={post.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        <article className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
          {/* Meta */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <Badge variant="outline">{post.category}</Badge>
            {post.featured && <Badge className="bg-yellow-100 text-yellow-700">Destaque</Badge>}
          </div>

          {/* Title */}
          <h1 className="text-3xl md:text-4xl font-bold text-[#0A0A0A] mb-4">
            {post.title}
          </h1>

          {/* Author & Date */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-8 pb-8 border-b border-[#E4E4E7]">
            <span className="flex items-center gap-2">
              <User className="h-4 w-4" />
              {post.author}
            </span>
            <span className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              {formatDate(post.published_at)}
            </span>
            <span className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              {getReadingTime(post.content)}
            </span>
            <span className="flex items-center gap-2">
              <Eye className="h-4 w-4" />
              {post.views} visualizações
            </span>
          </div>

          {/* Content */}
          <div 
            className="prose prose-lg max-w-none
              prose-headings:text-[#0A0A0A] prose-headings:font-bold
              prose-p:text-gray-700 prose-p:leading-relaxed
              prose-a:text-[#002FA7] prose-a:no-underline hover:prose-a:underline
              prose-strong:text-[#0A0A0A]
              prose-ul:text-gray-700 prose-ol:text-gray-700
              prose-blockquote:border-l-[#002FA7] prose-blockquote:text-gray-600 prose-blockquote:italic
              prose-code:bg-gray-100 prose-code:px-1 prose-code:rounded
              prose-img:rounded-lg prose-img:shadow-md"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="mt-8 pt-8 border-t border-[#E4E4E7]">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-gray-500">Tags:</span>
                {post.tags.map((tag, i) => (
                  <Badge 
                    key={i}
                    variant="outline"
                    className="cursor-pointer hover:bg-[#002FA7] hover:text-white transition-colors"
                    onClick={() => navigate(`/blog?tag=${encodeURIComponent(tag)}`)}
                  >
                    #{tag}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Share Section */}
          <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h3 className="font-bold text-[#0A0A0A]">Gostou do artigo?</h3>
                <p className="text-sm text-gray-600">Compartilhe com seus colegas pesquisadores!</p>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => window.open(shareLinks.whatsapp, '_blank')} className="bg-green-500 text-white border-green-500 hover:bg-green-600">
                  WhatsApp
                </Button>
                <Button variant="outline" size="sm" onClick={() => window.open(shareLinks.twitter, '_blank')}>
                  Twitter
                </Button>
                <Button variant="outline" size="sm" onClick={() => window.open(shareLinks.linkedin, '_blank')}>
                  LinkedIn
                </Button>
              </div>
            </div>
          </div>

          {/* Related Posts */}
          {relatedPosts.length > 0 && (
            <div className="mt-12">
              <h3 className="text-xl font-bold mb-6">Artigos relacionados</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {relatedPosts.map((related) => (
                  <article 
                    key={related.post_id}
                    onClick={() => navigate(`/blog/${related.slug}`)}
                    className="bg-white rounded-lg border border-[#E4E4E7] overflow-hidden hover:shadow-md cursor-pointer group"
                  >
                    {related.cover_image && (
                      <div className="aspect-video overflow-hidden">
                        <img 
                          src={related.cover_image} 
                          alt={related.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}
                    <div className="p-4">
                      <h4 className="font-bold line-clamp-2 group-hover:text-[#002FA7] transition-colors">
                        {related.title}
                      </h4>
                      <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
                        <span>{formatDate(related.published_at)}</span>
                        <ChevronRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="mt-12 text-center p-8 bg-gradient-to-br from-[#002FA7] to-[#0040D0] rounded-xl text-white">
            <h3 className="text-2xl font-bold mb-2">Encontre sua próxima publicação</h3>
            <p className="text-white/80 mb-6">Acesse nossa base de chamadas para publicação científica.</p>
            <Button
              onClick={() => navigate('/dashboard')}
              variant="secondary"
              size="lg"
            >
              Ver chamadas abertas
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
};

export default BlogPostPage;
